import { ReactNode, useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { TwoFactorSetup } from "@/components/auth/TwoFactorSetup";
import { TwoFactorVerify } from "@/components/auth/TwoFactorVerify";

type GateState = "loading" | "ok" | "missing" | "verify";

/**
 * Blocks admin areas until the signed-in user has a verified TOTP factor and the
 * current session passed it (AAL2). ISO 27001 A.8.5 / SOC 2 CC6.1
 */
export function RequireTwoFactor({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [state, setState] = useState<GateState>("loading");
  const [factorId, setFactorId] = useState("");
  const [setupOpen, setSetupOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const check = useCallback(async () => {
    if (!user?.id) return;
    const [{ data: factors }, { data: aal }] = await Promise.all([
      supabase.auth.mfa.listFactors(),
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
    ]);
    const verified = (factors?.totp ?? []).filter((f) => f.status === "verified");
    if (verified.length === 0) {
      setState("missing");
      return;
    }
    setFactorId(verified[0].id);
    setState(aal?.currentLevel === "aal2" ? "ok" : "verify");
  }, [user?.id]);

  useEffect(() => { check(); }, [check]);

  if (state === "loading") {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (state === "ok") return <>{children}</>;

  const needsCode = state === "verify";

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md space-y-4 rounded-xl border bg-card p-8 text-center shadow-sm">
        <ShieldCheck className="mx-auto h-12 w-12 text-primary" />
        <h1 className="text-xl font-semibold text-foreground">
          {needsCode ? "Introduce tu código de verificación" : "Verificación en dos pasos obligatoria"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {needsCode
            ? "Ya tienes la verificación en dos pasos activada. Introduce el código de 6 dígitos de tu app de autenticación para entrar."
            : "Para entrar en esta zona de administración debes activar la verificación en dos pasos con una app de autenticación."}
        </p>
        <Button
          className="min-h-[44px] w-full"
          onClick={() => (needsCode ? setVerifyOpen(true) : setSetupOpen(true))}
        >
          {needsCode ? "Introducir código" : "Activar ahora"}
        </Button>
      </div>
      <TwoFactorSetup open={setupOpen} onOpenChange={setSetupOpen} onSuccess={() => { setSetupOpen(false); check(); }} />
      {factorId && (
        <TwoFactorVerify
          open={verifyOpen}
          onOpenChange={setVerifyOpen}
          factorId={factorId}
          onSuccess={() => { setVerifyOpen(false); check(); }}
        />
      )}
    </div>
  );
}
